import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import { FiArrowLeft, FiMail } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { updateEmailManagementRequest } from '../../store/actions/emailManagementActions';
import 'bootstrap/dist/css/bootstrap.css';
import { getEmailManagementById } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import EmailManagementConfigFields from '../../components/Admin/EmailManagementConfigFields';
import EmailManagementTypeTabs from '../../components/Admin/EmailManagementTypeTabs';
import {
    EMAIL_LIST_ITEM_VALUES,
    getEmailListItemLabel,
} from '../../constants/emailListItems';
import {
    EMAIL_MANAGEMENT_CONFIG_DEFAULTS,
    EMAIL_MANAGEMENT_TYPE,
    applyEmailManagementTypeChange,
    buildEmailManagementPayload,
    getEmailManagementConfigSchema,
    inferEmailManagementType,
} from '../../constants/emailManagementForm';
import './AdminEmailEdit.css';

const emailManagementSchema = getEmailManagementConfigSchema();

const normalizeEmailList = (rawList) => {
    if (!Array.isArray(rawList)) return [];
    return rawList
        .map((item) => {
            if (typeof item === 'string') return item;
            if (item && typeof item === 'object') {
                return item.value || item.code || item.email_list_item || item.name || '';
            }
            return '';
        })
        .map((value) => String(value).trim())
        .filter((value) => EMAIL_LIST_ITEM_VALUES.includes(value));
};

const AdminEmailEdit = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { emailManagementId } = useParams();
    const { update } = useSelector((state) => state.emailManagement);

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [companyName, setCompanyName] = useState('');
    const [successMessage, setSuccessMessage] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
        watch,
        setValue,
    } = useForm({
        resolver: yupResolver(emailManagementSchema),
        defaultValues: {
            ...EMAIL_MANAGEMENT_CONFIG_DEFAULTS,
        },
    });

    const emailList = watch('email_list') || [];
    const emailManagementType = watch('email_management_type') || EMAIL_MANAGEMENT_TYPE.STANDARD;
    const isSaving = Boolean(update?.loading || isSubmitting);
    const isStandard = emailManagementType === EMAIL_MANAGEMENT_TYPE.STANDARD;

    useEffect(() => {
        const fetchEmailManagement = async () => {
            try {
                setLoading(true);
                setLoadError(null);
                const data = await getEmailManagementById(emailManagementId);

                const payload =
                    data?.data && (data.data.email_management || data.data.user)
                        ? data.data
                        : data;

                const emailMgmt =
                    payload?.email_management && typeof payload.email_management === 'object'
                        ? payload.email_management
                        : payload || {};

                const user = payload?.user || emailMgmt?.user || data?.user || {};

                setCompanyName(
                    user?.company_name ||
                        emailMgmt?.company_name ||
                        payload?.company_name ||
                        ''
                );

                reset({
                    email_management_type: inferEmailManagementType(emailMgmt),
                    email_process: emailMgmt.email_process || '',
                    recipient_type: emailMgmt.recipient_type || 'client',
                    email_list: normalizeEmailList(emailMgmt.email_list),
                });
            } catch (e) {
                setLoadError(
                    handleApiError(e, "Erreur lors du chargement de l'email management")
                );
            } finally {
                setLoading(false);
            }
        };

        if (emailManagementId) fetchEmailManagement();
    }, [emailManagementId, reset]);

    useEffect(() => {
        if (update?.success) {
            setSuccessMessage('Email management modifié avec succès !');
            setShowConfirmModal(false);
            setPendingPayload(null);
        }
        if (update?.error) {
            setSuccessMessage(null);
            setShowConfirmModal(false);
        }
    }, [update?.success, update?.error]);

    const onSubmit = (data) => {
        setPendingPayload(buildEmailManagementPayload(data));
        setShowConfirmModal(true);
    };

    const handleCloseConfirmModal = () => {
        if (update?.loading) return;
        setShowConfirmModal(false);
        setPendingPayload(null);
    };

    const handleConfirmSave = () => {
        if (!pendingPayload) return;
        dispatch(updateEmailManagementRequest(emailManagementId, pendingPayload));
    };

    const partnerLabel = companyName?.trim() || 'ce partenaire';
    const selectedEmails = Array.isArray(pendingPayload?.email_list)
        ? pendingPayload.email_list
        : emailList;

    if (loading) {
        return (
            <div className="dashboard-page admin-email-edit-page">
                <div className="aee-page">
                    <div className="aee-page__header">
                        <h1 className="aee-page__title">
                            <FiMail aria-hidden="true" />
                            Modifier l’Email Management
                        </h1>
                        <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                            <FiArrowLeft className="me-2" /> Retour
                        </Button>
                    </div>
                    <div className="aee-loading">
                        <Spinner animation="border" variant="primary" />
                        <p>Chargement de l’email management…</p>
                    </div>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="dashboard-page admin-email-edit-page">
                <div className="aee-page">
                    <div className="aee-page__header">
                        <h1 className="aee-page__title">
                            <FiMail aria-hidden="true" />
                            Modifier l’Email Management
                        </h1>
                        <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                            <FiArrowLeft className="me-2" /> Retour
                        </Button>
                    </div>
                    <Alert variant="danger">{loadError}</Alert>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-page admin-email-edit-page">
            <div className="aee-page">
                <div className="aee-page__header">
                    <h1 className="aee-page__title">
                        <FiMail aria-hidden="true" />
                        Modifier l’Email Management
                    </h1>
                    <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                        <FiArrowLeft className="me-2" /> Retour
                    </Button>
                </div>

                <div className="aee-type-tabs-wrap">
                    <EmailManagementTypeTabs
                        value={emailManagementType}
                        disabled={isSaving}
                        onChange={(value) => applyEmailManagementTypeChange(setValue, value)}
                    />
                </div>

                <div className="aee-partner" role="status">
                    Partenaire :{' '}
                    <strong>{companyName?.trim() || '—'}</strong>
                </div>

                {update?.error && (
                    <Alert variant="danger" className="mb-0" dismissible onClose={() => {}}>
                        {update.error}
                    </Alert>
                )}
                {successMessage && (
                    <Alert
                        variant="success"
                        className="mb-0"
                        dismissible
                        onClose={() => setSuccessMessage(null)}
                    >
                        {successMessage}
                    </Alert>
                )}

                <form className="aee-form" onSubmit={handleSubmit(onSubmit)} noValidate>
                    <input type="hidden" {...register('email_management_type')} />
                    <section className="aee-section" aria-labelledby="aee-section-title">
                        <header className="aee-section__header">
                            <h2 id="aee-section-title" className="aee-section__title">
                                {isStandard ? 'Configuration Standard' : 'Configuration Spécifique'}
                            </h2>
                            <p className="aee-section__desc">
                                {isStandard
                                    ? 'Renseignez les e-mails autorisés pour ce partenaire.'
                                    : 'Renseignez le processus email pour ce partenaire.'}
                            </p>
                        </header>

                        <div className="aee-section__body">
                            <EmailManagementConfigFields
                                register={register}
                                errors={errors}
                                setValue={setValue}
                                emailList={emailList}
                                isSaving={isSaving}
                                emailManagementType={emailManagementType}
                            />
                        </div>
                    </section>

                    <div className="aee-footer" role="group" aria-label="Actions du formulaire">
                        <div className="aee-footer__left">
                            {successMessage ? (
                                <Button
                                    variant="outline-primary"
                                    type="button"
                                    onClick={() => navigate(`/admin/email/${emailManagementId}`)}
                                >
                                    Voir les détails
                                </Button>
                            ) : null}
                        </div>
                        <div className="aee-footer__actions">
                            <Button
                                variant="outline-secondary"
                                type="button"
                                onClick={() => navigate(-1)}
                                disabled={isSaving}
                            >
                                Annuler
                            </Button>
                            <Button variant="primary" type="submit" disabled={isSaving}>
                                {isSaving ? (
                                    <>
                                        <Spinner size="sm" className="me-2" animation="border" />
                                        Enregistrement…
                                    </>
                                ) : (
                                    'Enregistrer'
                                )}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>

            <Modal show={showConfirmModal} onHide={handleCloseConfirmModal} centered>
                <Modal.Header closeButton={!update?.loading}>
                    <Modal.Title>Confirmer l’enregistrement</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {emailManagementType === EMAIL_MANAGEMENT_TYPE.SPECIFIQUE ? (
                        <p className="mb-0">
                            Vous êtes sur le point d’enregistrer le processus{' '}
                            <strong>{pendingPayload?.email_process || '—'}</strong> pour le
                            partenaire <strong>{partnerLabel}</strong>.
                        </p>
                    ) : (
                        <>
                            <p className="mb-3">
                                Vous êtes sur le point d’ajouter les e-mails suivants à l’envoi au
                                partenaire <strong>{partnerLabel}</strong>. Êtes-vous sûr d’avoir tout
                                sélectionné ?
                            </p>
                            {selectedEmails.length > 0 ? (
                                <ul className="mb-0 ps-3">
                                    {selectedEmails.map((value) => (
                                        <li key={`confirm-email-${value}`}>
                                            {getEmailListItemLabel(value)}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <Alert variant="warning" className="mb-0">
                                    Aucun e-mail n’est sélectionné.
                                </Alert>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={handleCloseConfirmModal}
                        disabled={update?.loading}
                    >
                        Annuler
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleConfirmSave}
                        disabled={update?.loading}
                    >
                        {update?.loading ? (
                            <>
                                <Spinner size="sm" className="me-2" animation="border" />
                                Enregistrement…
                            </>
                        ) : (
                            'Confirmer'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AdminEmailEdit;
